import {
  deleteAllEntities,
  entitiesPropsFactory,
  getAllEntitiesApply,
  setEntities,
  updateAllEntities,
  upsertEntitiesById
} from '@ngneat/elf-entities';
import {AxiosError, AxiosResponse} from 'axios';
import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';

import APIAxios, {APIRoutes} from '@api/axios.api';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {Pagination} from '@store/common/pagination.model';
import SnackError from '@utils/error.utils';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {
  JobInvoiceDetails,
  JobInvoiceStatus,
  JobsInvoicePagination
} from '@store/invoices/jobsInvoice/jobsInvoice.model';
import {
  getJobInvoiceDetailsDataSource,
  getJobsInvoiceDataSource
} from '@store/invoices/jobsInvoice/jobsInvoice.requests';
import {jobsInvoiceStore} from '@store/invoices/jobsInvoice/jobsInvoice.store';
import {filesService} from '@store/files';

const {invoicesEntitiesRef} = entitiesPropsFactory('invoices');
const {invoicesUIEntitiesRef} = entitiesPropsFactory('invoicesUI');

export class JobsInvoiceService {
  store = jobsInvoiceStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setStatus = (status: JobInvoiceStatus) => this.store.update((state) => ({
    ...state,
    status
  }));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    [name]: value
  }));

  getJobsInvoice = (): Observable<JobsInvoicePagination> => {
    const {sort, tablePagination, status, from: fromDate} = this.store.getValue();

    return from(APIAxios({
      ...APIRoutes.GETJobsInvoice(),
      params: {
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        status,
        from: fromDate?.toISOString() || undefined,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<JobsInvoicePagination>) => response.data),
        tap((resp) => {
          const paginationData = Pagination.toPaginationData(resp, tablePagination.page);
          this.store.update(
            setEntities(resp.data),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, resp.data.map((execution) => execution.id)),
            getJobsInvoiceDataSource.setSuccess(),
          );
        }),
        getJobsInvoiceDataSource.trackRequestStatus(),
      );
  };

  deleteEmptyJobInvoice = (jobInvoiceId: number): Observable<JobsInvoicePagination> => {
    return from(APIAxios(APIRoutes.DELETEJobInvoice(jobInvoiceId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getJobsInvoice()),
      );
  };

  getJobInvoiceDetails = (jobInvoiceId: number | string): Observable<JobInvoiceDetails> => {
    return from(APIAxios(APIRoutes.GETJobInvoiceId(jobInvoiceId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<JobInvoiceDetails>) => response.data),
        tap((jobInvoiceDetails) => {
          this.store.update(
            setEntities(jobInvoiceDetails.clientInvoices ?? [], {ref: invoicesEntitiesRef}),
            upsertEntitiesById((jobInvoiceDetails.clientInvoices ?? []).map((invoice) => invoice.id), {
              updater: (invoiceUi) => invoiceUi,
              creator: (invoiceId) => ({id: invoiceId, selected: true}),
              ref: invoicesUIEntitiesRef,
            }),
            getJobInvoiceDetailsDataSource.setSuccess(),
          );
        }),
        getJobInvoiceDetailsDataSource.trackRequestStatus(),
      );
  };

  setSelectedInvoices = (invoiceIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (invoiceUI) => ({...invoiceUI, selected: invoiceIds.includes(invoiceUI.id)}),
        {ref: invoicesUIEntitiesRef},
      )
    );
  };

  zipJobInvoice = (id: number | string): Observable<Blob> => {
    return from(APIAxios({
      ...APIRoutes.GETJobInvoiceZIP(id),
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/zip',
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Blob>) => response.data),
        tap((data) => filesService.downloadDocument(data, `job-invoice-${id}.zip`))
      );
  };

  csvJobInvoice = (id: number | string): Observable<Blob> => {
    return from(APIAxios({
      ...APIRoutes.GETJobInvoiceCSV(id),
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/zip',
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Blob>) => response.data),
        tap((data) => filesService.downloadDocument(data, `job-invoice-${id}.csv`)),
      );
  };

  printJobInvoice = (id: number | string): Observable<Blob> => {
    return from(APIAxios({
      ...APIRoutes.GETJobInvoicePDF(id),
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/pdf',
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Blob>) => response.data),
        tap((data) => filesService.downloadDocument(data, `job-invoice-${id}.pdf`))
      );
  };

  validateJobInvoice = (id: number | string): Observable<JobsInvoicePagination> => {
    return from(APIAxios({
      ...APIRoutes.PATCHValidateJobInvoice(id),
      data: {
        invoicesToDelete: this.store.query(
          getAllEntitiesApply({
            mapEntity: (e) => e.id,
            filterEntity: (e) => !e.selected,
            ref: invoicesUIEntitiesRef,
          }),
        ),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getJobsInvoice()),
      );
  };

  resetJobInvoiceDetails = () => {
    this.store.update(
      deleteAllEntities({ref: invoicesEntitiesRef}),
      deleteAllEntities({ref: invoicesUIEntitiesRef}),
    );
  };
}

export const jobsInvoiceService = new JobsInvoiceService();
